import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const categories = ['All', 'Furniture', 'Tableware', 'Lighting', 'Decor', 'AV & Sound'];

const equipment = [
  {
    id: 1,
    name: 'Chiavari Gold Chairs',
    category: 'Furniture',
    price: 12,
    unit: 'per chair/day',
    minQty: 20,
    image:
      'https://public.readdy.ai/ai/img_res/0a2e04e8389e30e03b3c56e4270c3f89.jpg',
    popular: true,
  },
  {
    id: 2,
    name: 'Round Banquet Tables',
    category: 'Furniture',
    price: 45,
    unit: 'per table/day',
    minQty: 5,
    image:
      'https://readdy.ai/api/search-image?query=elegant%20round%20banquet%20table%20with%20white%20linen%20tablecloth%20on%20clean%20white%20background%2C%20premium%20event%20furniture%20rental%2C%20professional%20product%20photography%20luxury%20dining%20table%2C%20simple%20studio%20lighting&width=400&height=400&seq=equip-table-001&orientation=squarish',
    popular: true,
  },
  {
    id: 3,
    name: 'Crystal Glassware Set',
    category: 'Tableware',
    price: 5,
    unit: 'per set/day',
    minQty: 50,
    image:
      'https://readdy.ai/api/search-image?query=premium%20crystal%20wine%20glasses%20and%20champagne%20flutes%20set%20on%20clean%20white%20background%2C%20elegant%20glassware%20collection%2C%20professional%20product%20photography%20luxury%20event%20tableware%2C%20simple%20studio%20lighting&width=400&height=400&seq=equip-glass-001&orientation=squarish',
    popular: false,
  },
  {
    id: 4,
    name: 'LED Uplighting Package',
    category: 'Lighting',
    price: 250,
    unit: 'per event',
    minQty: 1,
    image:
      'https://readdy.ai/api/search-image?query=professional%20LED%20uplighting%20equipment%20for%20events%2C%20warm%20amber%20and%20white%20LED%20lights%20on%20clean%20white%20background%2C%20event%20lighting%20rental%20equipment%2C%20professional%20product%20photography%20simple%20studio%20lighting&width=400&height=400&seq=equip-light-001&orientation=squarish',
    popular: true,
  },
  {
    id: 5,
    name: 'Festoon String Lights',
    category: 'Lighting',
    price: 85,
    unit: 'per 20m strand',
    minQty: 3,
    image:
      'https://readdy.ai/api/search-image?query=warm%20festoon%20string%20lights%20bulbs%20coiled%20on%20clean%20white%20background%2C%20outdoor%20event%20lighting%2C%20vintage%20Edison%20bulb%20string%20lights%2C%20professional%20product%20photography%20simple%20studio%20lighting&width=400&height=400&seq=equip-festoon-001&orientation=squarish',
    popular: false,
  },
  {
    id: 6,
    name: 'Gold Cutlery Collection',
    category: 'Tableware',
    price: 4,
    unit: 'per setting/day',
    minQty: 50,
    image:
      'https://readdy.ai/api/search-image?query=premium%20gold%20cutlery%20set%20with%20knife%20fork%20spoon%20on%20clean%20white%20background%2C%20elegant%20event%20tableware%20rental%2C%20professional%20product%20photography%20luxury%20dining%20flatware%2C%20simple%20studio%20lighting&width=400&height=400&seq=equip-cutlery-001&orientation=squarish',
    popular: false,
  },
  {
    id: 7,
    name: 'Floral Arch Frame',
    category: 'Decor',
    price: 180,
    unit: 'per event',
    minQty: 1,
    image:
      'https://readdy.ai/api/search-image?query=elegant%20gold%20metal%20wedding%20arch%20frame%20on%20clean%20white%20background%2C%20ceremony%20backdrop%20structure%2C%20premium%20event%20decor%20rental%20professional%20product%20photography%20simple%20studio%20lighting&width=400&height=400&seq=equip-arch-001&orientation=squarish',
    popular: true,
  },
  {
    id: 8,
    name: 'PA Sound System',
    category: 'AV & Sound',
    price: 350,
    unit: 'per event',
    minQty: 1,
    image:
      'https://readdy.ai/api/search-image?query=professional%20PA%20speaker%20system%20and%20microphone%20on%20clean%20white%20background%2C%20event%20audio%20equipment%20rental%20professional%20sound%20system%20product%20photography%20simple%20studio%20lighting&width=400&height=400&seq=equip-sound-001&orientation=squarish',
    popular: false,
  },
  {
    id: 9,
    name: 'Velvet Lounge Sofa',
    category: 'Furniture',
    price: 120,
    unit: 'per piece/day',
    minQty: 1,
    image:
      'https://readdy.ai/api/search-image?query=elegant%20emerald%20green%20velvet%20lounge%20sofa%20on%20clean%20white%20background%2C%20premium%20event%20furniture%20rental%20luxury%20seating%20professional%20product%20photography%20simple%20studio%20lighting&width=400&height=400&seq=equip-sofa-001&orientation=squarish',
    popular: false,
  },
];

export default function Equipment() {
  const [activeCategory, setActiveCategory] = useState('All');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({
    rootMargin: '0px 0px -50px 0px',
  });

  const filtered =
    activeCategory === 'All'
      ? equipment
      : equipment.filter((item) => item.category === activeCategory);

  const scrollToInquiry = () => {
    const el = document.getElementById('inquiry');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="equipment" className="py-32 bg-stone-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-amber-500"></div>
            <span className="text-amber-400 text-xs font-semibold tracking-[0.3em] uppercase">
              Premium Inventory
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-amber-500"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-display font-normal text-white mb-6">
            Hire Our{' '}
            <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-100 to-amber-300">
              Equipment
            </span>
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto text-lg leading-relaxed">
            Browse our curated collection of premium event equipment. All prices include delivery,
            setup, and collection.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-14">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-white shadow-lg shadow-amber-500/20'
                  : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/30'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Equipment Grid */}
        <div
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children ${gridVisible ? 'visible' : ''}`}
          data-product-shop
        >
          {filtered.map((item) => (
            <div
              key={item.id}
              className="group relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] rounded-2xl overflow-hidden border border-white/5 hover:border-amber-500/30 transition-all duration-500 cursor-pointer"
            >
              {/* Image */}
              <div className="relative h-56 bg-gradient-to-br from-stone-800 to-stone-900 p-6">
                {item.popular && (
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-amber-400 text-white text-xs font-semibold rounded-full shadow-lg z-10">
                    Popular
                  </div>
                )}
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <span className="text-amber-400/60 text-xs tracking-wider uppercase">
                  {item.category}
                </span>
                <h3 className="text-lg font-serif font-semibold text-white mt-1 mb-2 group-hover:text-amber-200 transition-colors">
                  {item.name}
                </h3>

                <div className="flex items-center gap-2 text-sm text-white/40 mb-4">
                  <i className="ri-stack-line text-amber-400"></i>
                  <span>
                    Min. {item.minQty} {item.minQty === 1 ? 'unit' : 'units'}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <div>
                    <span className="text-2xl font-display text-white">${item.price}</span>
                    <span className="text-white/40 text-xs ml-1">{item.unit}</span>
                  </div>
                  <button
                    onClick={scrollToInquiry}
                    className="px-4 py-2 bg-white/10 border border-white/10 text-white text-xs font-medium rounded-full hover:bg-amber-500 hover:border-amber-500 transition-all whitespace-nowrap"
                  >
                    Enquire
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 p-8 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-amber-500/10 rounded-2xl border border-amber-500/20">
            <div className="text-center sm:text-left">
              <p className="text-white font-display text-xl mb-1">Need a custom package?</p>
              <p className="text-white/50 text-sm">
                We create bespoke equipment packages for any event size
              </p>
            </div>
            <button
              onClick={scrollToInquiry}
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-400 text-white rounded-full hover:from-amber-400 hover:to-amber-300 transition-all whitespace-nowrap flex items-center gap-2 text-sm font-semibold shadow-lg shadow-amber-500/20"
            >
              <i className="ri-chat-1-line"></i>
              Get Custom Quote
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
